/**
 * @module services/zotero/apiClient
 * @description Base API client for Zotero API requests
 *
 * Routes all requests through Supabase Edge Function proxy.
 * API keys are managed server-side in Spectral Cloud Portal for security.
 */

import { api } from '@/config';
import { createLogger } from '../loggerService';
import {
  proxyZoteroRequest,
  isZoteroConfigured as checkZoteroConfigured,
  isAuthenticated as isSupabaseAuthenticated,
} from '../supabaseClient';
import { parseLinkHeader } from './helpers';
import type { RequestConfig, ApiResponseInternal } from './types';

const log = createLogger('api');

// =============================================================================
// ZOTERO API CLIENT
// =============================================================================

/**
 * Zotero API Client
 *
 * Routes all requests through Supabase Edge Function proxy.
 * API keys are stored server-side in Spectral Cloud Portal for security.
 */
class ZoteroApiClient {
  // ===========================================================================
  // AUTHENTICATION
  // ===========================================================================

  /**
   * Check if authenticated via Supabase
   */
  async isAuthenticatedAsync(): Promise<boolean> {
    const isAuth = await isSupabaseAuthenticated();
    if (!isAuth) return false;
    return checkZoteroConfigured();
  }

  // ===========================================================================
  // API REQUESTS
  // ===========================================================================

  /**
   * Make an API request via proxy
   */
  async request<T>(config: RequestConfig): Promise<ApiResponseInternal<T>> {
    log.debug('API request', {
      method: config.method,
      path: config.path,
      params: config.params,
    });

    return this.requestViaProxy<T>(config);
  }

  /**
   * Make a request via Supabase Edge Function proxy
   */
  private async requestViaProxy<T>(config: RequestConfig): Promise<ApiResponseInternal<T>> {
    const { method, path, params = {}, body, headers = {}, signal } = config;

    try {
      const response = await proxyZoteroRequest<T>({
        path,
        method,
        body,
        params,
        zoteroHeaders: headers,
        signal,
      });

      return {
        data: response.data,
        totalResults: response.headers.totalResults || 0,
        lastModifiedVersion: response.headers.lastModifiedVersion || 0,
        links: parseLinkHeader(response.headers.link || null),
      };
    } catch (error) {
      if (error instanceof Error) {
        log.error('Proxy request failed', { path, error: error.message });
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  // ===========================================================================
  // EXPORT REQUESTS (returns text, not JSON)
  // ===========================================================================

  /**
   * Make an export request via proxy (returns text content)
   */
  async requestExport(config: RequestConfig): Promise<string> {
    const { method, path, params = {} } = config;

    log.debug('Export request', { method, path, params });

    try {
      const response = await proxyZoteroRequest<string>({
        path,
        method,
        params,
        responseType: 'text',
      });

      return typeof response.data === 'string' ? response.data : String(response.data);
    } catch (error) {
      if (error instanceof Error) {
        log.error('Export proxy request failed', { path, error: error.message });
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/** Singleton API client instance */
export const apiClient = new ZoteroApiClient();

/** Export class for testing */
export { ZoteroApiClient };
